import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import Card, { Kicker } from "../brand/Surface";
import Action from "../brand/Action";
import Reveal from "../brand/Reveal";
import { ME } from "../../data/cv";

/**
 * The privacy statement.
 *
 * Deliberately short, because the site does deliberately little: there is no
 * analytics, no tracker and no cookie on this domain, so there is nothing to
 * ask consent for beyond the contact form itself. Everything below has to stay
 * literally true — a statement that overpromises is worse than none.
 *
 * The date is hard-coded. `new Date()` would claim the page was reviewed today
 * every day, which is exactly the kind of thing this page must not say.
 */
const UPDATED = "16 augustus 2026";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy · Casper De Bock";
  }, []);

  return (
    <div className="mx-auto max-w-[760px]">
      <Reveal>
        <Kicker>Privacy</Kicker>
        <h1 className="max-w-[16ch]">Wat ik met je gegevens doe.</h1>
        <p className="mt-5 max-w-[58ch] text-[15px]">
          Kort, want deze site doet weinig: geen analytics, geen trackers, geen cookies. Het enige
          moment waarop ik iets van je bijhoud, is wanneer je zelf het contactformulier invult.
        </p>
        <p className="kicker mt-6">Laatst bijgewerkt: {UPDATED}</p>
      </Reveal>

      <Reveal stagger className="mt-8 grid gap-4">
        <Section title="Wie is verantwoordelijk">
          <p>
            {ME.name}, {ME.place}. Te bereiken op{" "}
            <Mail /> — ook voor alles wat hieronder staat.
          </p>
        </Section>

        <Section title="Welke gegevens, en waarom">
          <p>
            Alleen wat je zelf in het contactformulier typt: je naam, je e-mailadres, het onderwerp
            dat je aanklikt en je bericht. Ik gebruik dat om je antwoord te sturen, en voor niets
            anders. Geen nieuwsbrief, geen doorverkoop, geen profielopbouw.
          </p>
          <p>
            De rechtsgrond is je toestemming, die je geeft door het vakje aan te vinken voor je
            verstuurt. Je kan die op elk moment intrekken door me te mailen.
          </p>
        </Section>

        <Section title="Wie het nog ziet">
          <p>
            Het formulier wordt verstuurd via <Ext href="https://www.emailjs.com/legal/privacy-policy/">EmailJS</Ext>,
            dat het bericht als e-mail doorstuurt. De mail komt aan in mijn Gmail-mailbox bij Google.
            Dat zijn de enige twee partijen die je bericht onderweg zien. Verder deel ik niets met
            iemand.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            Deze site plaatst geen cookies en meet je bezoek niet. Er staat één ding in de opslag van
            je browser — je keuze tussen de lichte en de donkere versie — en dat blijft op je eigen
            toestel staan. Ik kan het niet uitlezen.
          </p>
        </Section>

        <Section title="Hoe lang ik het bijhoud">
          <p>
            Je bericht blijft in mijn mailbox staan zolang het gesprek nuttig is, en maximaal twee
            jaar na het laatste contact. Vraag je me eerder om het te wissen, dan wis ik het.
          </p>
        </Section>

        <Section title="Je rechten">
          <p>
            Je mag opvragen wat ik van je heb, het laten verbeteren, laten wissen of bezwaar maken
            tegen het gebruik ervan. Eén mail volstaat en ik antwoord binnen de maand.
          </p>
          <p>
            Ben je het niet eens met hoe ik dat doe, dan kan je klacht indienen bij de Belgische
            Gegevensbeschermingsautoriteit,{" "}
            <Ext href="https://www.gegevensbeschermingsautoriteit.be/">
              gegevensbeschermingsautoriteit.be
            </Ext>
            , Drukpersstraat 35, 1000 Brussel.
          </p>
        </Section>
      </Reveal>

      <div className="mt-8">
        <Action to="/contact" variant="ghost">
          <ArrowLeft aria-hidden className="size-4" strokeWidth={2} />
          Terug naar contact
        </Action>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card pad="lg">
      <h2 className="font-display text-[19px] leading-tight font-medium tracking-[-0.04em] text-fg">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-[14.5px]">{children}</div>
    </Card>
  );
}

function Mail() {
  return (
    <a
      href={`mailto:${ME.email}`}
      className="font-semibold text-accent-text underline underline-offset-2"
    >
      {ME.email}
    </a>
  );
}

function Ext({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-semibold text-accent-text underline underline-offset-2"
    >
      {children}
    </a>
  );
}
